import React, { FC, useState } from "react";
import ClipboardHelper from "react-copy-to-clipboard";
import { Input, InputProps, Tooltip } from "@xapp/components";
import { FontIcon } from "react-toolbox/lib/font_icon";
import { Omit } from "utils/Types";

import style from "./style.scss";

export interface CopyToClipboardProps {
    /**
     * Text for the tooltip after the text was copied
     */
    copiedTooltipText?: string;
    /**
     * Text for the tooltip before the text was copied
     */
    tooltipText?: string;
    /**
     * Properties passed through to the Input
     */
    inputProps?: Omit<InputProps, "value" | "disabled">;
    /**
     * The text to be copied by the user
     */
    textToCopy: string;
    /**
     * Callback when the user clicks to copy.
     *
     * @param text The text that was copied
     * @param success If it was successfully copied
     */
    onCopy?(text: string, success: boolean): void;
}

/**
 * A simple input field that when the user clicks on it the contents are copied to their clipboard.
 *
 * @param props
 */
const CopyToClipboard: FC<CopyToClipboardProps> = (props: CopyToClipboardProps) => {
    const {
        copiedTooltipText: copiedText,
        tooltipText: helpText,
        inputProps,
        textToCopy,
        onCopy } = props;

    const [copied, setCopied] = useState<boolean>(false);

    const help = helpText || "Click to copy to the clipboard";
    const copiedHelp = copied ? copiedText || "COPIED!" : "";

    function handleCopy(text: string, result: boolean) {
        setCopied(result);
        if (onCopy) {
            onCopy(text, result);
        }
        if (result) {
            setTimeout(() => {
                setCopied(false);
            }, 10000);
        }
    }

    const lines = textToCopy ? textToCopy.split('\n').length : 1;

    return (
        <ClipboardHelper text={textToCopy} onCopy={handleCopy}>
            <div style={{ display: "flex" }}>
                <span>
                    <Tooltip content={copied ? copiedHelp : help}>
                        <FontIcon value={copied ? "check" : "content_copy"} />
                    </Tooltip>
                </span>
                <Input
                    className={style.input}
                    value={textToCopy || ""}
                    {...inputProps}
                    disabled
                    multiline={lines > 1}
                    rows={2}
                />
            </div>
        </ClipboardHelper>
    );
};

export { CopyToClipboard };
